import type { SVGProps } from "react";

/**
 * Canonical contour paths for the portrait-topography mark
 * (outermost → innermost), from brand/mark-primary.svg.
 */
const CONTOUR_PATHS = [
  // Contour 4 (outermost) — overall head shape
  "M 220 52 C 300 40, 370 72, 404 132 C 424 168, 428 200, 420 230 C 414 254, 398 268, 394 288 C 390 304, 400 316, 408 332 C 414 344, 406 358, 390 370 C 378 380, 380 396, 388 416 C 394 432, 380 454, 348 466 C 316 478, 272 476, 232 468 C 172 450, 116 400, 96 332 C 80 280, 84 220, 108 164 C 140 92, 180 60, 220 52 Z",
  // Contour 3 — forehead slope and nose bridge
  "M 228 100 C 292 90, 348 112, 376 160 C 394 192, 396 218, 388 242 C 382 260, 370 272, 368 290 C 366 306, 374 318, 380 332 C 386 344, 378 356, 364 366 C 354 374, 356 390, 362 408 C 368 422, 356 440, 330 448 C 300 456, 264 454, 240 446 C 196 432, 156 394, 142 340 C 130 296, 132 248, 148 200 C 168 140, 198 108, 228 100 Z",
  // Contour 2 — brow, nose, upper lip, chin
  "M 236 156 C 282 148, 322 164, 344 200 C 358 224, 360 246, 352 264 C 346 278, 338 288, 338 304 C 338 316, 348 326, 354 338 C 358 348, 350 358, 338 366 C 330 372, 334 386, 340 400 C 344 412, 334 426, 314 432 C 290 438, 264 436, 246 430 C 216 420, 192 396, 184 358 C 176 326, 178 288, 188 250 C 200 202, 218 168, 236 156 Z",
  // Contour 1 (innermost) — gold core
  "M 244 220 C 270 214, 296 226, 310 250 C 320 268, 320 282, 316 296 C 312 306, 308 314, 310 326 C 312 334, 320 342, 324 352 C 328 360, 322 370, 312 376 C 304 380, 308 392, 314 404 C 318 412, 310 422, 296 426 C 278 430, 264 428, 254 424 C 236 416, 224 402, 220 382 C 216 360, 216 334, 222 306 C 228 270, 236 240, 244 220 Z",
] as const;

/**
 * Total length of each contour path (outermost → innermost), in viewBox
 * units, for the draw-in animation's stroke-dasharray/offset. Measured by
 * dense cubic-bezier sampling of CONTOUR_PATHS (5000 samples per segment):
 * 1246.44 / 1012.37 / 774.51 / 543.10, rounded up so the dash always covers
 * the full path. Re-measure if CONTOUR_PATHS ever change.
 */
const CONTOUR_LENGTHS = [1247, 1013, 775, 544] as const;

/**
 * Draw-in animation classes per contour (outermost → innermost). Written out
 * literally (not constructed) so Tailwind's scanner sees them; the numeric
 * suffixes bake in the 0.18s stagger (see --animate-contour-draw* in
 * src/index.css).
 */
const CONTOUR_DRAW_CLASSES = [
  "animate-contour-draw",
  "animate-contour-draw-1",
  "animate-contour-draw-2",
  "animate-contour-draw-3",
] as const;

/**
 * Hover-breathe animation classes per contour ring (outermost → innermost),
 * applied to a wrapping <g> so the scale pulse composes with (rather than
 * replaces) the path's own draw-in animation. Triggered by hovering a
 * `group`-classed ancestor (see Wordmark hoverAnimate).
 */
const CONTOUR_BREATHE_CLASSES = [
  "contour-ring group-hover:animate-contour-breathe",
  "contour-ring group-hover:animate-contour-breathe-1",
  "contour-ring group-hover:animate-contour-breathe-2",
  "contour-ring group-hover:animate-contour-breathe-3",
] as const;

/** Per-contour stroke treatment (outermost → innermost) for each mark variant. */
const CONTOUR_STROKES = {
  // Amber gradient per brand/mark-primary.svg (asset colors, not UI tokens).
  primary: [
    { stroke: "#92400e", strokeOpacity: 1 },
    { stroke: "#b45309", strokeOpacity: 1 },
    { stroke: "#d97706", strokeOpacity: 1 },
    { stroke: "#f59e0b", strokeOpacity: 1 },
  ],
  // Single-color with opacity steps per brand/mark-monochrome.svg —
  // inherits currentColor, for colored/gradient backgrounds.
  monochrome: [
    { stroke: "currentColor", strokeOpacity: 0.3 },
    { stroke: "currentColor", strokeOpacity: 0.5 },
    { stroke: "currentColor", strokeOpacity: 0.7 },
    { stroke: "currentColor", strokeOpacity: 1 },
  ],
} as const;

interface TraitprintMarkProps extends SVGProps<SVGSVGElement> {
  /**
   * "primary" — amber contour gradient (default).
   * "monochrome" — currentColor with opacity steps, for colored backgrounds.
   */
  variant?: keyof typeof CONTOUR_STROKES;
  /**
   * "draw" — opt-in load animation: each contour draws in via
   * stroke-dasharray/offset, staggered outermost → innermost. The `forwards`
   * end state (and the reduced-motion instant-complete state) is the fully
   * drawn mark, identical to rendering without the prop.
   */
  animate?: "draw";
  /**
   * Opt-in hover "breathe": rings scale-pulse outward in sequence when a
   * `group`-classed ancestor is hovered (Wordmark adds `group` itself when
   * its own hoverAnimate prop is set). At rest the rings sit at scale(1).
   */
  hoverAnimate?: boolean;
}

/**
 * Portrait-topography SVG mark.
 * 4 concentric contour lines resolving into an abstract human profile.
 * Scales cleanly at h-5 through h-8 in nav contexts.
 */
export function TraitprintMark({
  className,
  variant = "primary",
  animate,
  hoverAnimate = false,
  ...props
}: TraitprintMarkProps) {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
      {...props}
    >
      {CONTOUR_PATHS.map((d, i) => {
        const key = d.slice(0, 16);
        const path = (
          <path
            key={key}
            d={d}
            stroke={CONTOUR_STROKES[variant][i].stroke}
            strokeOpacity={CONTOUR_STROKES[variant][i].strokeOpacity}
            strokeWidth="5.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className={animate === "draw" ? CONTOUR_DRAW_CLASSES[i] : undefined}
            style={
              animate === "draw"
                ? {
                    strokeDasharray: `${CONTOUR_LENGTHS[i]}`,
                    strokeDashoffset: `${CONTOUR_LENGTHS[i]}`,
                  }
                : undefined
            }
          />
        );
        // The breathe pulse lives on a wrapping <g> so its transform composes
        // with the path's draw-in animation instead of replacing it.
        return hoverAnimate ? (
          <g key={key} className={CONTOUR_BREATHE_CLASSES[i]}>
            {path}
          </g>
        ) : (
          path
        );
      })}
    </svg>
  );
}

interface WordmarkProps {
  /** Additional classes on the wrapper element */
  className?: string;
  /** Opt-in contour draw-in load animation on the mark. */
  animate?: "draw";
  /** Opt-in hover breathe: hovering the wordmark scale-pulses the mark's rings. */
  hoverAnimate?: boolean;
}

/**
 * Traitprint brand wordmark.
 * Combines the portrait-topography mark with the "traitprint.com" logotype in Space Grotesk.
 */
export function Wordmark({ className = "", animate, hoverAnimate = false }: WordmarkProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-brand-600 dark:text-brand-400 select-none ${
        hoverAnimate ? "group " : ""
      }${className}`}
      aria-label="Traitprint"
    >
      <TraitprintMark
        className="h-6 w-6 flex-shrink-0"
        animate={animate}
        hoverAnimate={hoverAnimate}
      />
      <span className="text-xl font-bold tracking-tight leading-none font-display">
        traitprint<span className="text-brand-400 dark:text-brand-400">.com</span>
      </span>
    </span>
  );
}
